import { gql } from '@apollo/client';

import {
  BillingAccountFullFragment,
  CouponFragment,
  DeliveryDayFragment,
  DeliveryOptionFragment,
  DeliveryPeriodFragment,
  DietFragment,
  ErrorFragment,
  InvoiceBasicFragment,
  MotivationFragment,
  IngredientTagFragment,
  MarketingNoticeFragment,
  MarketingVideoFragment,
  NotificationPreferencesFragment,
  OrderFragment,
  PrepaidProgramFragment,
  PrepaidPurchaseFragment,
  ProgramBundleFragment,
  ProgramBundleReservationFragment,
  PriceFragment,
  ProductCategoryFragment,
  ProductFullFragment,
  ProductImageFragment,
  ProductDetailFragment,
  ProductPreviewFragment,
  ProductSizeFragment,
  ProductSubcategoryFragment,
  ProductTagFragment,
  ProgramScheduleFragment,
  ProgramSizeFragment,
  RateableOrderFragment,
  SplitTestAssignmentFragment,
  SubscriptionFragment,
  SubscriptionCategoryFragment,
  SubscriptionOptionsFragment,
  TastePreferencesFragment,
  UserFragment,
  VariantFullFragment,
  ProgramMealTypeFragment,
  OneTimeProgramFragment,
} from './fragments';

export const CURRENT_USER = gql`
  query CurrentUser {
    currentUser {
      ...UserFragment
    }
  }
  ${UserFragment}
`;

export const GET_ALERTS = gql`
  query GetAlerts {
    alerts @client {
      id
      message
      autoClose
    }
  }
`;

export const GET_BACKGROUND_JOB = gql`
  query GetBackgroundJob($backgroundJobId: ID!) {
    getBackgroundJob(backgroundJobId: $backgroundJobId) {
      id
      status
      updateTime
      errors {
        ...ErrorFragment
      }
    }
  }
  ${ErrorFragment}
`;

export const GET_BILLING_INFO = gql`
  query GetBillingInfo {
    getBillingInfo {
      ...BillingAccountFullFragment
    }
  }
  ${BillingAccountFullFragment}
`;

export const GET_DELIVERY_DAY = gql`
  query GetDeliveryDay($deliveryDayId: ID!) {
    getDeliveryDay(deliveryDayId: $deliveryDayId) {
      ...DeliveryDayFragment
    }
  }
  ${DeliveryDayFragment}
`;

export const GET_FIRST_ORDER = gql`
  query {
    getFirstOrder {
      ...OrderFragment
      deliveryDay {
        id
      }
    }
  }
  ${OrderFragment}
`;

export const GET_REFERRAL_MODAL_OPENED = gql`
  query {
    isReferralModalOpen @client
  }
`;

export const GET_MENU_OPENED = gql`
  query {
    isMenuOpen @client
  }
`;

export const GET_NOTIFICATION_PREFERENCES = gql`
  query GetNotificationPreferences {
    getNotificationPreferences {
      ...NotificationPreferencesFragment
    }
  }
  ${NotificationPreferencesFragment}
`;

export const GET_NEXT_CUTOFF_TIME_FOR_PROGRAM_SUBSCRIPTION = gql`
  query GetNextCutoffTimeForProgramSubscription($deliveryDayId: ID!) {
    getNextCutoffTimeForProgramSubscription(deliveryDayId: $deliveryDayId)
  }
`;

export const GET_ONE_TIME_PROGRAM = gql`
  query GetOneTimeProgram {
    getOneTimeProgram {
      programBundle {
        ...ProgramBundleFragment
      }
      bundleReservations {
        ...ProgramBundleReservationFragment
      }
    }
  }
  ${ProgramBundleFragment}
  ${ProgramBundleReservationFragment}
`;

export const GET_ORDER = gql`
  query GetOrder($orderId: ID!) {
    getOrder(orderId: $orderId) {
      ...OrderFragment
    }
  }
  ${OrderFragment}
`;

export const GET_ORDER_DATE = gql`
  query GetOrderDate($orderId: ID!) {
    getOrder(orderId: $orderId) {
      id
      date
    }
  }
`;

export const GET_PREPAID_PURCHASE = gql`
  query GetPrepaidPurchase {
    getPrepaidPurchase {
      ...PrepaidPurchaseFragment
    }
  }
  ${PrepaidPurchaseFragment}
`;

export const GET_LIST_ADDONS = gql`
  query GetListAddons($deliveryDayId: ID!) {
    listAddons(deliveryDayId: $deliveryDayId) {
      id
      allergens
      dietTags
      cost {
        amount
      }
      day
      freezerFriendly
      name
      images {
        smallUrl
        largeUrl
      }
      subtitle
      quantity
    }
  }
`;

export const GET_COMPONENT_DETAIL = gql`
  query GetComponentDetail($componentId: ID!, $variantId: ID) {
    getComponentDetail(componentId: $componentId, variantId: $variantId) {
      id
      name
      allergens
      dietTags
      subtitle
      shelfLife
      tagLine
      ingredients {
        name
      }
      images {
        smallUrl
        largeUrl
      }
      instructions
      nutrition {
        calories
        fatGrams
        carbsGrams
        fiberGrams
        sugarGrams
        sugarAlcoholGrams
        proteinGrams
      }
      variants {
        id
        displayName
      }
      variantId
      day
      allergens
    }
  }
`;

export const GET_PRODUCT = gql`
  query GetProduct($productId: ID!, $deliveryDayId: ID!, $zipcode: String) {
    getProduct(productId: $productId, deliveryDayId: $deliveryDayId, zipcode: $zipcode) {
      ...ProductFullFragment
    }
  }
  ${ProductFullFragment}
`;

export const GET_PRODUCT_FILTERS = gql`
  query GetProductFilters {
    productFilters @client
  }
`;

export const GET_PRODUCT_TAG = gql`
  query GetProductTag($productTagId: ID!) {
    getProductTag(productTagId: $productTagId) {
      ...ProductTagFragment
    }
  }
  ${ProductTagFragment}
`;

export const GET_PRODUCT_VARIANT = gql`
  query GetProductVariant($productVariantId: ID!, $zipcode: String) {
    getProductVariant(productVariantId: $productVariantId, zipcode: $zipcode) {
      ...VariantFullFragment
    }
  }
  ${VariantFullFragment}
`;

export const GET_PROGRAM_BUNDLE = gql`
  query GetProgramBundle($programId: ID!, $sizeId: ID!, $scheduleId: ID!, $mealTypeId: ID!, $numWeeks: Int = 1, $otpSubscribed: Boolean, $deliveryDayId: ID) {
    getProgramBundle(programId: $programId, sizeId: $sizeId, scheduleId: $scheduleId, mealTypeId: $mealTypeId, numWeeks: $numWeeks, otpSubscribed: $otpSubscribed, deliveryDayId: $deliveryDayId) {
      ...ProgramBundleFragment
    }
  }
  ${ProgramBundleFragment}
`;

export const LIST_DELIVERY_DAYS_FOR_DIGITAL_ONLY = gql`
query ListDeliveryDaysForDigitalOnly {
  listDeliveryDaysForDigitalOnly {
    ...DeliveryDayFragment
    dateWithoutOffset
  }
}
${DeliveryDayFragment}
`;

export const GET_RATING_MODAL_ORDER_ID = gql`
  query {
    ratingModalOrderId @client
  }
`;

export const GET_SPLIT_TEST_ASSIGNMENTS = gql`
  query GetSplitTestAssignments($testNames: [String!]!, $readonly: Boolean) {
    getSplitTestAssignments(testNames: $testNames, readonly: $readonly) {
      ...SplitTestAssignmentFragment
    }
  }
  ${SplitTestAssignmentFragment}
`;

export const GET_SUBSCRIPTION = gql`
  query GetSubscription {
    getSubscription {
      ...SubscriptionFragment
    }
  }
  ${SubscriptionFragment}
`;

export const GET_SUBSCRIPTION_OPTIONS = gql`
  query GetSubscriptionOptions {
    getSubscriptionOptions {
      ...SubscriptionOptionsFragment
    }
  }
  ${SubscriptionOptionsFragment}
`;

export const GET_NEXT_CUTOFF_TIME = gql`
  query GetNextCutoffTime {
    getNextCutoffTime
  }
`;

export const GET_SUBSCRIPTION_PURCHASE = gql`
  query GetSubscriptionPurchase {
    getSubscriptionPurchase {
      numItems
      deliveryFee {
        ...PriceFragment
      }
      serviceFee {
        ...PriceFragment
      }
      subtotal {
        ...PriceFragment
      }
      coupon {
        ...CouponFragment
      }
      discount {
        ...PriceFragment
      }
      total {
        ...PriceFragment
      }
      orders {
        id
        date
        deliveryDay {
          id
        }
      }
    }
  }
  ${PriceFragment}
  ${CouponFragment}
`;

export const GET_TASTE_PREFERENCES = gql`
  query GetTastePreferences {
    listFoodsToAvoid {
      ...IngredientTagFragment
    }
    currentUser {
      ...TastePreferencesFragment
    }
  }
  ${IngredientTagFragment}
  ${TastePreferencesFragment}
`;

export const GET_VIDEO_ID = gql`
  query {
    videoId @client
  }
`;

export const GET_PROMOCODE_INFO = `
  query GetPromocodeInfo($promocode: String!) {
    getPromocodeInfo(promocode: $promocode) {
      code
      discountPercentage
      discountType
      durationType
      applicableOnWeek
      discountAmount {
        amount
      }
    }
  }
`;

export const GET_USER_SWAPS_DELIVERY_DAY = gql`
  query GetUserSwapsForDeliveryDay($deliveryDayIds: String!) {
    getUserSwapsForDeliveryDay(deliveryDayIds: $deliveryDayIds) {
      dayId
      addons {
        id
        requestType
        originalComponentName
        price {
          amount
        }
        quantity
      }
      swaps {
        id
        requestType
        originalComponentName
        replacedComponentName
        price {
          amount
        }
        quantity
      }
    }
  }
`;

export const LIST_DELIVERY_PERIODS = gql`
  query ListDeliveryPeriods($startDate: Iso8601Date!, $endDate: Iso8601Date!) {
    listDeliveryPeriods(startDate: $startDate, endDate: $endDate) {
      ...DeliveryPeriodFragment
    }
  }
  ${DeliveryPeriodFragment}
`;

export const LIST_UNLOCKED_AVAILABLE_DELIVERY_DAYS = gql`
  query ListUnlockedAvailableDeliveryDays {
    listUnlockedAvailableDeliveryDays {
      id
      date
      locked
      cutoffTime
      menuPublished
      programCutoffPassed
    }
  }
`;

export const GET_PRODUCT_COMPONENTS_BY_DELIVERY_DAY = gql`
  query GetProductComponentsByDeliveryDay($deliveryDayId: ID!) {
    getProductComponentsByDeliveryDay(deliveryDayId: $deliveryDayId) {
      id
      name
      subtitle
      freezerFriendly
      day
      allergens
      editable
      swapped
      images {
        smallUrl
        largeUrl
      }
    }
  }
`;

export const GET_SWAPPED_PRODUCT_COMPONENTS_BY_DELIVERY_DAY = gql`
  query GetSwappedProductComponentsByDeliveryDay($deliveryDayId: ID!) {
    getSwappedProductComponentsByDeliveryDay(deliveryDayId: $deliveryDayId) {
      id
      name
      subtitle
      freezerFriendly
      day
      allergens
      editable
      swapped
      originalComponentId
      images {
        smallUrl
        largeUrl
      }
    }
  }
`;

export const GET_USER_SWAP_BILLING_SUMMARY = gql`
  query GetUserSwapBillingSummary($deliveryDayIds: String!) {
    getUserSwapBillingSummary(deliveryDayIds: $deliveryDayIds) {
      creditsTotalAmount {
        amount
      }
      swapsTotalAmount {
        amount
      }
      addonsTotalAmount {
        amount
      }
      finalAmount {
        amount
      }
      invoiceId
      totalSwapsCount
      totalAddonsCount
    }
  }
`;

export const GET_ELIGIBLE_MEAL_SWAPS = gql`
  query GetEligibleMealSwaps($deliveryDayId: ID!, $mealComponentId: ID!) {
    getEligibleMealSwaps(deliveryDayId: $deliveryDayId, mealComponentId: $mealComponentId) {
      id
      name
      variantId
      variants {
        id
        displayName
      }
      cost {
        amount
      }
      dietTags
      images {
        smallUrl
        largeUrl
      }
    }
  }
`;

export const FETCH_USER_DELIVERY_SKIPS = gql`
  query FetchUserDeliverySkips {
    fetchUserDeliverySkips {
      deliveryPeriod {
        id
        startDate
        endDate
        locked
        assigned
      }
      skipped
      canSkip
      canUnskip
    }
  }
`;

export const GET_PROGRAM_DETAIL = gql`
  query GetProgramDetail {
    getProgramDetail {
      ...OneTimeProgramFragment
    }
  }
  ${OneTimeProgramFragment}
`;

export const LIST_DIETS = gql`
  query ListDiets {
    listDiets {
      ...DietFragment
    }
  }
  ${DietFragment}
`;

export const LIST_FIRST_WEEK_ORDERS = gql`
  query GetSubscriptionPurchaseOrders {
    getSubscriptionPurchase {
      confirmedOrders {
        id
        date
        cutoffTime
        numItems
        billedAmount {
          ...PriceFragment
        }
        deliveryOption {
          ...DeliveryOptionFragment
        }
      }
    }
  }
  ${PriceFragment}
  ${DeliveryOptionFragment}
`;

export const LIST_FOODS_TO_AVOID = gql`
  query ListFoodsToAvoid {
    listFoodsToAvoid {
      ...IngredientTagFragment
    }
  }
  ${IngredientTagFragment}
`;

export const LIST_GLOBAL_FOODS_TO_AVOID = gql`
  query ListGlobalFoodsToAvoid {
    listGlobalFoodsToAvoid {
      ...IngredientTagFragment
    }
  }
  ${IngredientTagFragment}
`;

export const LIST_INVOICES = gql`
  query ListInvoices {
    listInvoices {
      ...InvoiceBasicFragment
    }
  }
  ${InvoiceBasicFragment}
`;

export const LIST_MARKETING_NOTICES = gql`
  query ListMarketingNotices {
    listMarketingNotices {
      ...MarketingNoticeFragment
    }
  }
  ${MarketingNoticeFragment}
`;

export const LIST_MARKETING_VIDEOS = gql`
  query ListMarketingVideos {
    listMarketingVideos {
      ...MarketingVideoFragment
    }
  }
  ${MarketingVideoFragment}
`;

export const LIST_PRODUCT_SIZES = gql`
  query ListProductSizes {
    listProductSizes {
      ...ProductSizeFragment
    }
  }
  ${ProductSizeFragment}
`;

export const LIST_MOTIVATIONS = gql`
  query ListMotivations {
    listMotivations {
      ...MotivationFragment
    }
  }
  ${MotivationFragment}
`;

export const LIST_PRODUCT_CATEGORIES = gql`
  query ListProductCategories($deliveryDayId: ID!) {
    listProductCategories(deliveryDayId: $deliveryDayId) {
      ...ProductCategoryFragment
      subcategories {
        ...ProductSubcategoryFragment
      }
    }
  }
  ${ProductCategoryFragment}
  ${ProductSubcategoryFragment}
`;

export const LIST_PRODUCT_INVENTORY_STATUSES = gql`
  query ListProductInventoryStatuses($deliveryDayId: ID!) {
    listProductInventoryStatuses(deliveryDayId: $deliveryDayId) {
      id
      soldOut
      product {
        id
      }
    }
  }
`;

export const LIST_PRODUCT_TAGS = gql`
  query ListProductTags($deliveryDayId: ID!) {
    listProductTags(deliveryDayId: $deliveryDayId) {
      ...ProductTagFragment
    }
  }
  ${ProductTagFragment}
`;

export const LIST_PRODUCT_PREVIEWS = gql`
  query ListProducts($deliveryDayId: ID!, $productCategoryId: ID, $productTagId: ID, $ordered: Boolean, $featured: Boolean, $onlyReset: Boolean) {
    listProducts(
      deliveryDayId: $deliveryDayId,
      productCategoryId: $productCategoryId,
      productTagId: $productTagId,
      ordered: $ordered,
      featured: $featured,
      onlyReset: $onlyReset
    ) {
      ...ProductPreviewFragment
    }
  }
  ${ProductPreviewFragment}
`;

export const LIST_PRODUCT_DETAILS = gql`
  query ListProducts($deliveryDayId: ID!, $productCategoryId: ID, $productTagId: ID, $ordered: Boolean, $featured: Boolean) {
    listProducts(
      deliveryDayId: $deliveryDayId,
      productCategoryId: $productCategoryId,
      productTagId: $productTagId,
      ordered: $ordered,
      featured: $featured
    ) {
      ...ProductDetailFragment
    }
  }
  ${ProductDetailFragment}
`;

export const LIST_PROGRAM_OPTIONS = gql`
  query ListProgramOptions {
    listPrepaidPrograms {
      ...PrepaidProgramFragment
    }
    listProgramSizes {
      ...ProgramSizeFragment
    }
    listProgramSchedules {
      ...ProgramScheduleFragment
    }
          listProgramMealTypes {
      ...ProgramMealTypeFragment
    }
  }
  ${PrepaidProgramFragment}
  ${ProgramSizeFragment}
  ${ProgramScheduleFragment}
  ${ProgramMealTypeFragment}
`;

export const LIST_RATEABLE_ORDERS = gql`
  query ListRateableOrders($startDate: Iso8601Date!, $endDate: Iso8601Date!) {
    listRateableOrders(startDate: $startDate, endDate: $endDate) {
      ...RateableOrderFragment
    }
  }
  ${RateableOrderFragment}
`;

export const LIST_RATEABLE_PRODUCT_VARIANTS = gql`
  query ListRateableProductVariants($orderId: ID!) {
    listRateableProductVariants(orderId: $orderId) {
      id
      product {
        name
        primaryImage {
          ...ProductImageFragment
        }
      }
      components {
        id
        name
        subtitle
        tagLine
        parentSku
      }
    }
  }
  ${ProductImageFragment}
`;

export const LIST_SUBSCRIPTION_QUANTITY_OPTIONS = gql`
  query ListSubscriptionQuantityOptions {
    listSubscriptionDays {
      index
      name
    }
    listSubscriptionCategories {
      ...SubscriptionCategoryFragment
    }
  }
  ${SubscriptionCategoryFragment}
`;

export const LIST_USER_ADDRESSES = gql`
  query ListUserAddresses {
    listUserAddresses {
      id
      priority
      primary
      street1
      street2
      city
      state
      zipCode
      country
      instructions
    }
  }
`;

export const GET_INVOICED_USER_SWAPS = gql`
  query GetInvoicedUserSwaps($deliveryDayIds: String!, $invoiceId: String) {
    getInvoicedUserSwaps(deliveryDayIds: $deliveryDayIds, invoiceId: $invoiceId) {
      dayId
      invoicePdfUrl
      addons {
        id
        requestType
        originalComponentName
        price {
          amount
        }
        quantity
      }
      swaps {
        id
        requestType
        originalComponentName
        replacedComponentName
        price {
          amount
        }
        quantity
      }
    }
  }
`;

export const LIST_PAID_SIGNATURE_PROGRAM_ORDERS = gql`
  query ListPaidSignatureProgramOrders {
    listPaidSignatureProgramOrders {
      id
      dummy
      date
      status
      image {
        smallUrl
      }
      trackingUrl
      deliveryDayGqlId
    }
  }
`;

export const GET_ORDER_DETAILS = gql`
  query GetOrderDetails($orderId: ID!) {
    getOrderDetails(orderId: $orderId) {
      id
      dummy
      date
      image {
        smallUrl
      }
      status
      trackingUrl
      deliveryDayGqlId
      billingTime
      bundleReservation {
        price {
          amount
        }
      }
      estimatedAmount {
        amount
      }
      billedAmount {
        amount
      }
      lineItems {
        quantity
        product {
          name
          subtitle
          tagLine
          description
        }
      }
      bundleInvoicePdfUrl
      oneTimeProgram {
        ...OneTimeProgramFragment
      }
    }
  }
  ${OneTimeProgramFragment}
`;

export const GET_RESET_PASSWORD_TOKEN = gql`
  query GetResetPasswordToken($email: String!) {
    getResetPasswordToken(email: $email)
  }
`;

export const LIST_HEALTH_GOALS = gql`
  query ListHealthGoals {
    listHealthGoals {
      id
      name
      adminName
      description
      icon
      position
    }
  }
`;

export const GET_USER_HEALTH_GOALS = gql`
  query GetUserHealthGoals {
    currentUser {
      id
      healthProfile {
        id
        healthGoals {
          id
          name
        }
      }
    }
  }
`;

export const LIST_AGE_RANGES = gql`
  query ListAgeRanges {
    listAgeRanges {
      id
      name
      adminName
      minAge
      maxAge
    }
  }
`;

export const LIST_FOOD_MOTIVATIONS = gql`
  query ListFoodMotivations {
    listFoodMotivations {
      id
      name
      adminName
      description
      position
    }
  }
`;

export const LIST_CUISINES = gql`
  query ListCuisines {
    listCuisines {
      id
      name
      position
    }
  }
`;

export const LIST_PROTEINS = gql`
  query ListProteins {
    listProteins {
      id
      name
      adminName
      userFacing
    }
  }
`;

export const LIST_ONRAMP_FOODS_TO_AVOID = gql`
  query ListFoodsToAvoid($isNewOnramp: Boolean = true) {
    listFoodsToAvoid(isNewOnramp: $isNewOnramp) {
      ...IngredientTagFragment
    }
  }
  ${IngredientTagFragment}
`;

export const LIST_BLOODWORK_MARKER_CATEGORIES = gql`
  query ListBloodworkMarkerCategories {
    listBloodworkMarkerCategories {
      id
      name
      adminName
      position
      bloodworkMarkers {
        id
        name
        adminName
        position
      }
    }
  }
`;

export const GET_SPLIT_TEST_ORDER = gql`
  query GetSplitTestOrder($oneTimeProgramId: ID!) {
    getSplitTestOrder(oneTimeProgramId: $oneTimeProgramId) {
      id
      orderId
      productVariantName
      cuisine
      score
      calories
      proteinGrams
    }
  }
`;

export const LIST_SUBSCRIPTION_STATUS_REASONS = gql`
  query ListSubscriptionStatusReasons {
    listSubscriptionStatusReasons {
      id
      reason
      category
      requireMemo
    }
  }
`;

export const GET_USER_BLOODWORK_MARKERS = gql`
  query ListUserBloodworkMarkers {
    listUserBloodworkMarkers {
      status
      bloodworkMarker {
        id
        name
        adminName
        position
      }
    }
  }
`;

export const LIST_HOMEPAGE_MEALS = gql`
  query ListHomepageMeals {
    listHomepageMeals {
      name
      calories
      proteinG
      photoUrl
    }
  }
`;

export const LIST_ONRAMP_FOOD_GROUPS = gql`
  query ListOnrampFoodGroups($programName: String) {
    listOnrampFoodGroups(programName: $programName) {
      key
      ingredientTags {
        id
        adminName
        name
      }
    }
  }
`;
